import React, { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { LoadZoneObjectName } from "./LoadZoneActualLoadVisual";
import {
  getLoadZoneMaxItems,
  getLoadZoneSeriesColor,
  matchesSelectedIso,
  normalizeObjectName,
  pickTopLoadZoneObjects,
} from "./loadZoneUtils";
import { MARKET_MONITOR_TOOLTIP_PROPS } from "./tooltipConfig";

interface MarketDataRow {
  ISO?: string;
  OBJECTNAME?: string | null;
  TIMESTAMP?: string;
  DATETIME?: string;
  DATA_POINTS?: string;
  VALUE?: string | number | null;
  VOLUME?: string | number | null;
}

interface LoadZonePoint {
  index: number;
  timestampIso: string;
  timestampLabel: string;
  dateLabel: string;
  [key: string]: string | number | null;
}

interface LoadZoneForecastVisualProps {
  renewableForecastRows: MarketDataRow[];
  selectedIso: string;
  selectedObjectName: LoadZoneObjectName | null;
  onSelectObjectName: (name: LoadZoneObjectName) => void;
}

const MAX_POINTS = 14 * 24;
const FORECAST_DATA_POINT = "forecast_renewable_gen";
const DIMMED_COLOR = "#cbd5e1";
const PJM_FORECAST_SERIES_KEY = "PJM TOTAL";

const toNumber = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toHourKey = (value: string | undefined): string | null => {
  if (!value) {
    return null;
  }

  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const parsed = new Date(normalized);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  parsed.setMinutes(0, 0, 0);
  return parsed.toISOString();
};

const toShortDate = (isoDateTime: string): string => {
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) {
    return isoDateTime;
  }

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
  });
};

const toFullTimeLabel = (isoDateTime: string): string => {
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) {
    return isoDateTime;
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const roundValue = (value: number): number => Math.round(value * 100) / 100;

const formatYAxis = (value: number): string => `${Math.round(value / 1000)}K`;

const formatTooltip = (value: number): string =>
  `${value.toLocaleString("en-US", { maximumFractionDigits: 0 })} MW`;

const isPjmIso = (selectedIso: string): boolean => {
  const iso = selectedIso.trim().toUpperCase();
  return iso === "PJM" || iso === "PJMISO";
};

const buildChartData = (
  rows: MarketDataRow[],
  selectedIso: string
): { data: LoadZonePoint[]; objectNames: string[] } => {
  if (isPjmIso(selectedIso)) {
    const totalByHour = new Map<string, number>();

    rows.forEach((row) => {
      if (!matchesSelectedIso(row.ISO, selectedIso)) {
        return;
      }

      const dataPoint = row.DATA_POINTS?.toLowerCase().trim();
      if (dataPoint && dataPoint !== FORECAST_DATA_POINT) {
        return;
      }

      const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
      const value = toNumber(row.VALUE);
      if (!hourKey || value === null) {
        return;
      }

      totalByHour.set(hourKey, (totalByHour.get(hourKey) ?? 0) + value);
    });

    const timeline = Array.from(totalByHour.keys()).sort().slice(-MAX_POINTS);
    const data = timeline.map((hourKey, index) => ({
      index,
      timestampIso: hourKey,
      timestampLabel: toFullTimeLabel(hourKey),
      dateLabel: toShortDate(hourKey),
      [PJM_FORECAST_SERIES_KEY]: roundValue(totalByHour.get(hourKey) ?? 0),
    }));

    return { data, objectNames: [PJM_FORECAST_SERIES_KEY] };
  }

  const buckets = new Map<string, Map<string, number>>();
  const objectTotals = new Map<string, number>();

  rows.forEach((row) => {
    if (!matchesSelectedIso(row.ISO, selectedIso)) {
      return;
    }

    const objectName = normalizeObjectName(row.OBJECTNAME);
    if (!objectName) {
      return;
    }

    const dataPoint = row.DATA_POINTS?.toLowerCase().trim();
    if (dataPoint && dataPoint !== FORECAST_DATA_POINT) {
      return;
    }

    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VALUE ?? row.VOLUME);

    if (!hourKey || value === null) {
      return;
    }

    const hourBucket = buckets.get(hourKey) ?? new Map<string, number>();
    hourBucket.set(objectName, (hourBucket.get(objectName) ?? 0) + value);
    buckets.set(hourKey, hourBucket);
    objectTotals.set(objectName, (objectTotals.get(objectName) ?? 0) + Math.abs(value));
  });

  const objectNames = pickTopLoadZoneObjects(
    objectTotals,
    selectedIso,
    getLoadZoneMaxItems(selectedIso)
  );

  if (!objectNames.length) {
    return { data: [], objectNames: [] };
  }

  const timeline = Array.from(buckets.keys()).sort().slice(-MAX_POINTS);

  const data = timeline.map((hourKey, index) => {
    const hourBucket = buckets.get(hourKey) ?? new Map<string, number>();

    const point: LoadZonePoint = {
      index,
      timestampIso: hourKey,
      timestampLabel: toFullTimeLabel(hourKey),
      dateLabel: toShortDate(hourKey),
    };

    objectNames.forEach((name) => {
      const hourlySum = hourBucket.get(name);
      point[name] = hourlySum !== undefined ? roundValue(hourlySum) : null;
    });

    return point;
  });

  return { data, objectNames };
};

const LoadZoneForecastVisual: React.FC<LoadZoneForecastVisualProps> = ({
  renewableForecastRows,
  selectedIso,
  selectedObjectName,
  onSelectObjectName,
}) => {
  const { data, objectNames } = useMemo(
    () => buildChartData(renewableForecastRows, selectedIso),
    [renewableForecastRows, selectedIso]
  );
  const isPjmForecastAggregate = isPjmIso(selectedIso);

  const hasActiveSelection = !!(selectedObjectName && objectNames.includes(selectedObjectName));

  const getSeriesColor = (name: LoadZoneObjectName): string =>
    hasActiveSelection && selectedObjectName !== name
      ? DIMMED_COLOR
      : getLoadZoneSeriesColor(objectNames.indexOf(name));

  const getSeriesOpacity = (name: LoadZoneObjectName): number =>
    hasActiveSelection && selectedObjectName !== name ? 0.45 : 1;

  const handleLegendClick = (entry: unknown) => {
    if (isPjmForecastAggregate) {
      return;
    }

    const legendEntry = entry as { value?: unknown; dataKey?: unknown };
    const selected = String(legendEntry?.value ?? legendEntry?.dataKey ?? "").toUpperCase();
    if (objectNames.includes(selected)) {
      onSelectObjectName(selected);
    }
  };

  const xTicks = useMemo(
    () => data.filter((item) => item.index % 48 === 0 || item.index === data.length - 1).map((item) => item.index),
    [data]
  );

  if (!data.length || !objectNames.length) {
    return (
      <div className="w-full h-[280px] rounded-xl border border-gray-200 bg-white p-4 flex items-center justify-center text-gray-500">
        No forecast data available.
      </div>
    );
  }

  return (
    <div className="w-full h-[280px] xl:h-auto xl:flex-[28] rounded-xl border border-gray-200 bg-white p-4">
      <h2 className="text-lg font-semibold text-gray-700 mb-2">Forecast</h2>

      <ResponsiveContainer width="100%" height="84%">
        <LineChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
          <XAxis
            dataKey="index"
            tickFormatter={(value) => data[value]?.dateLabel ?? ""}
            ticks={xTicks}
            tick={{ fill: "#6b7280", fontSize: 12 }}
          />
          <YAxis
            domain={[0, "auto"]}
            tickFormatter={formatYAxis}
            tick={{ fill: "#6b7280", fontSize: 12 }}
            label={{ value: "MW", angle: -90, position: "insideLeft", offset: 0 }}
          />
          <Tooltip
            {...MARKET_MONITOR_TOOLTIP_PROPS}
            formatter={(value: number) => formatTooltip(value)}
            labelFormatter={(value) => data[value]?.timestampLabel ?? ""}
          />
          <Legend
            verticalAlign="top"
            align="left"
            wrapperStyle={{ paddingBottom: 8, cursor: "pointer" }}
            onClick={handleLegendClick}
            formatter={(value: string) => {
              const normalized = value.toUpperCase();
              const isDimmed = !!(
                hasActiveSelection &&
                objectNames.includes(normalized) &&
                selectedObjectName !== normalized
              );

              return (
                <span style={{ color: isDimmed ? "#94a3b8" : "#374151", opacity: isDimmed ? 0.65 : 1 }}>
                  {value}
                </span>
              );
            }}
          />
          {objectNames.map((name) => (
            <Line
              key={name}
              type="monotone"
              dataKey={name}
              name={name}
              stroke={getSeriesColor(name)}
              strokeOpacity={getSeriesOpacity(name)}
              strokeWidth={selectedObjectName === name ? 3.2 : 2.6}
              dot={false}
              onClick={isPjmForecastAggregate ? undefined : () => onSelectObjectName(name)}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LoadZoneForecastVisual;
